import React, { useEffect, useState } from "react";
import { FaFacebookF, FaInstagram, FaLinkedinIn, FaMinus, FaPlus } from "react-icons/fa";
import { useNavigate, Link } from "react-router-dom";
import { Sparkles, Search as SearchIcon } from "lucide-react";
import RouteCard from "../components/card";
import AIChatAssistantCard from "../components/card2";
import Card3 from "../components/card3";
import Card4 from "../components/card4";
import { FormSearchSection, TripSearchSection } from "../components/home";
import "../styles/home2.css";
import { FEATURES } from "../config/features";
import { CREATORS, getCreatorTrips } from "../creator/creatorTripsData";

import ahmedabadImg from "../assets/images/Popular_flights/ahmedabad.png";
import bangloreImg from "../assets/images/Popular_flights/banglore.png";
import chennaiImg from "../assets/images/Popular_flights/chennai.png";
import delhiImg from "../assets/images/Popular_flights/delhi.png";
import goaImg from "../assets/images/Popular_flights/goa.png";
import hydrabadImg from "../assets/images/Popular_flights/hydrabad.png";
import kolkataImg from "../assets/images/Popular_flights/kolkata.png";
import mumbaiImg from "../assets/images/Popular_flights/mumbai.png";
import puneImg from "../assets/images/Popular_flights/pune.png";



const Home2 = () => {
    const [activeFaq, setActiveFaq] = useState(null);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const navigate = useNavigate();
    const [heroInput, setHeroInput] = useState("");

    /* ── Search mode toggle: "routes" (Search Routes) | "trip" (Create a Trip, beta) ── */
    const [searchTab, setSearchTab] = useState("routes");

    const faqs = [
        {
            question: "How does Routechef suggest a confirmed seat route?",
            answer: (
                <>
                    Routechef uses a combination of smart techniques to help you find confirmed seat options—even when direct train routes are waitlisted:
                    <ul>
                        <li>✅ Break journeys using either trains alone or a mix of trains and flights to uncover better availability</li>
                        <li>🚉 Book from the train's origin or to its final destination to access special quotas, while still allowing you to board and deboard at your preferred stations</li>
                        <li>⏱️ Suggest routes with realistic layover windows, factoring in historical delay patterns and commute times between stations and airports</li>
                        <li>📊 Instantly compare availability and prices across train and flight legs to choose the most reliable and cost-effective option</li>
                    </ul>
                    By optimizing routes this way, Routechef helps you beat the waitlist — without needing insider knowledge or hours of searching.
                </>
            ),
        },
        {
            question: "What kind of routes does Routechef suggest?",
            answer: (
                <ul>
                    <li>Direct Train and Flight routes</li>
                    <li>Break journeys (Train + Train) via popular junctions/cities</li>
                    <li>Train + Flight combinations</li>
                    <li>Routes from nearby stations if that increases your chances of a confirmed seat route</li>
                </ul>
            ),
        },
        {
            question: "Can I use Routechef if I already booked a waitlisted ticket?",
            answer: (
                <p>Yes! Routechef is most useful after you get waitlisted. You can find alternate break-journey routes or switch to other transport modes — without cancelling your original ticket until you're sure.</p>
            ),
        },
        {
            question: "Can Routechef help with emergency travel (same-day)?",
            answer: (
                <p>Absolutely. Routechef is ideal for last-minute travelers. If direct options are full, it finds immediate alternatives with confirmed availability — even if it means using a connecting city or switching modes.</p>
            ),
        },
        {
            question: "What makes Routechef better than other route planners?",
            answer: (
                <ul>
                    <li>Unlike traditional apps that stop at "train not available or waitlisted" Routechef: Doubles your chance of finding a confirmed seat</li>
                    <li>Works across train and flight routes</li>
                    <li>Plans smart one-stop connections where trains are full</li>
                    <li>Search routes in your local language (like Hindi, Tamil, etc.) using our WhatsApp/AI assistant — just chat naturally, and we'll handle the rest!</li>
                    <li>Focuses on low-cost options, not just fastest or most expensive ones</li>
                </ul>
            ),
        },
    ];

    const popularFlightRoutes = [
        {
            name: "Mumbai Flights",
            image: mumbaiImg,
            routes: [
                { name: "Goa", url: "https://routechef.com/search-form?from=Mumbai+Airport+%28BOM%29&to=Goa+Airport+%28GOI%29&dcode=GOI&slat=19.089&slng=72.868&dlat=15.381&dlng=73.833&stype=airport&dtype=airport&date=2026-03-27&adults=1&children=0&infants=0&seatClasses=Economy%2C3A&modes=Flight&nonstop=0" },
                { name: "Delhi", url: "https://routechef.com/search-form?from=Mumbai+Airport+%28BOM%29&to=Delhi+Airport+%28DEL%29&dcode=DEL&slat=19.089&slng=72.868&dlat=28.566&dlng=77.103&stype=airport&dtype=airport&date=2026-03-27&adults=1&children=0&infants=0&seatClasses=Economy%2C3A&modes=Flight&nonstop=0" },
                { name: "Bangalore", url: "https://routechef.com/search-form?from=Mumbai+Airport+%28BOM%29&to=Bangalore+Airport+%28BLR%29&dcode=BLR&slat=19.089&slng=72.868&dlat=12.95&dlng=77.668&stype=airport&dtype=airport&date=2026-03-27&adults=1&children=0&infants=0&seatClasses=Economy%2C3A&modes=Flight&nonstop=0" },
                { name: "Ahmedabad", url: "https://routechef.com/search-form?from=Mumbai+Airport+%28BOM%29&to=Ahmedabad+Airport+%28AMD%29&dcode=AMD&slat=19.089&slng=72.868&dlat=23.075&dlng=72.632&stype=airport&dtype=airport&date=2026-03-27&adults=1&children=0&infants=0&seatClasses=Economy%2C3A&modes=Flight&nonstop=0" }
            ]
        },
        {
            name: "Delhi Flights",
            image: delhiImg,
            routes: [
                { name: "Mumbai", url: "https://routechef.com/search-form?from=Delhi+Airport+%28DEL%29&to=Mumbai+Airport+%28BOM%29&dcode=BOM&slat=28.566&slng=77.103&dlat=19.089&dlng=72.868&stype=airport&dtype=airport&date=2026-03-27&adults=1&children=0&infants=0&seatClasses=Economy%2C3A&modes=Flight&nonstop=0" },
                { name: "Goa", url: "https://routechef.com/search-form?from=Delhi+Airport+%28DEL%29&to=Goa+Airport+%28GOI%29&dcode=GOI&slat=28.566&slng=77.103&dlat=15.381&dlng=73.833&stype=airport&dtype=airport&date=2026-03-27&adults=1&children=0&infants=0&seatClasses=Economy%2C3A&modes=Flight&nonstop=0" },
                { name: "Bangalore", url: "https://routechef.com/search-form?from=Delhi+Airport+%28DEL%29&to=Bangalore+Airport+%28BLR%29&dcode=BLR&slat=28.566&slng=77.103&dlat=12.95&dlng=77.668&stype=airport&dtype=airport&date=2026-03-27&adults=1&children=0&infants=0&seatClasses=Economy%2C3A&modes=Flight&nonstop=0" },
                { name: "Pune", url: "https://routechef.com/search-form?from=Delhi+Airport+%28DEL%29&to=Pune+Airport+%28PNQ%29&dcode=PNQ&slat=28.566&slng=77.103&dlat=18.582&dlng=73.919&stype=airport&dtype=airport&date=2026-03-27&adults=1&children=0&infants=0&seatClasses=Economy%2C3A&modes=Flight&nonstop=0" }
            ]
        },
        {
            name: "Kolkata Flights",
            image: kolkataImg,
            routes: [
                { name: "Mumbai", url: "https://routechef.com/search-form?from=Kolkata+Airport+%28CCU%29&to=Mumbai+Airport+%28BOM%29&dcode=BOM&slat=22.655&slng=88.447&dlat=19.089&dlng=72.868&stype=airport&dtype=airport&date=2026-03-27&adults=1&children=0&infants=0&seatClasses=Economy%2C3A&modes=Flight&nonstop=0" },
                { name: "Delhi", url: "https://routechef.com/search-form?from=Kolkata+Airport+%28CCU%29&to=Delhi+Airport+%28DEL%29&dcode=DEL&slat=22.655&slng=88.447&dlat=28.566&dlng=77.103&stype=airport&dtype=airport&date=2026-03-27&adults=1&children=0&infants=0&seatClasses=Economy%2C3A&modes=Flight&nonstop=0" },
                { name: "Bangalore", url: "https://routechef.com/search-form?from=Kolkata+Airport+%28CCU%29&to=Bangalore+Airport+%28BLR%29&dcode=BLR&slat=22.655&slng=88.447&dlat=12.95&dlng=77.668&stype=airport&dtype=airport&date=2026-03-27&adults=1&children=0&infants=0&seatClasses=Economy%2C3A&modes=Flight&nonstop=0" },
                { name: "Bagdogra", url: "https://routechef.com/search-form?from=Kolkata+Airport+%28CCU%29&to=Bagdogra+Airport+%28IXB%29&dcode=IXB&slat=22.655&slng=88.447&dlat=26.681&dlng=88.328&stype=airport&dtype=airport&date=2026-03-27&adults=1&children=0&infants=0&seatClasses=Economy%2C3A&modes=Flight&nonstop=0" }
            ]
        },
        {
            name: "Chennai Flights",
            image: chennaiImg,
            routes: [
                { name: "Mumbai", url: "https://routechef.com/search-form?from=Chennai+Airport+%28MAA%29&to=Mumbai+Airport+%28BOM%29&dcode=BOM&slat=12.994&slng=80.18&dlat=19.089&dlng=72.868&stype=airport&dtype=airport&date=2026-03-27&adults=1&children=0&infants=0&seatClasses=Economy%2C3A&modes=Flight&nonstop=0" },
                { name: "Delhi", url: "https://routechef.com/search-form?from=Chennai+Airport+%28MAA%29&to=Delhi+Airport+%28DEL%29&dcode=DEL&slat=12.994&slng=80.18&dlat=28.566&dlng=77.103&stype=airport&dtype=airport&date=2026-03-27&adults=1&children=0&infants=0&seatClasses=Economy%2C3A&modes=Flight&nonstop=0" },
                { name: "Madurai", url: "https://routechef.com/search-form?from=Chennai+Airport+%28MAA%29&to=Madurai+Airport+%28IXM%29&dcode=IXM&slat=12.994&slng=80.18&dlat=9.834&dlng=78.093&stype=airport&dtype=airport&date=2026-03-27&adults=1&children=0&infants=0&seatClasses=Economy%2C3A&modes=Flight&nonstop=0" },
                { name: "Coimbatore", url: "https://routechef.com/search-form?from=Chennai+Airport+%28MAA%29&to=Coimbatore+Airport+%28CJB%29&dcode=CJB&slat=12.994&slng=80.18&dlat=11.031&dlng=77.044&stype=airport&dtype=airport&date=2026-03-27&adults=1&children=0&infants=0&seatClasses=Economy%2C3A&modes=Flight&nonstop=0" }
            ]
        },
        {
            name: "Hyderabad Flights",
            image: hydrabadImg,
            routes: [
                {
                    name: "Mumbai",
                    url: "https://routechef.com/search-form?from=Hyderabad+Airport+%28HYB%29&to=Mumbai+Airport+%28BOM%29&dcode=BOM&dlat=19.089&dlng=72.868&stype=airport&dtype=airport&date=2026-03-27&adults=1&children=0&infants=0&seatClasses=Economy%2C3A&modes=Flight&nonstop=0"
                },
                {
                    name: "Goa",
                    url: "https://routechef.com/search-form?from=Hyderabad+Airport+%28HYB%29&to=Goa+Airport+%28GOI%29&dcode=GOI&dlat=15.381&dlng=73.833&stype=airport&dtype=airport&date=2026-03-27&adults=1&children=0&infants=0&seatClasses=Economy%2C3A&modes=Flight&nonstop=0"
                },
                {
                    name: "Bangalore",
                    url: "https://routechef.com/search-form?from=Hyderabad+Airport+%28HYB%29&to=Bangalore+Airport+%28BLR%29&dcode=BLR&dlat=12.95&dlng=77.668&stype=airport&dtype=airport&date=2026-03-27&adults=1&children=0&infants=0&seatClasses=Economy%2C3A&modes=Flight&nonstop=0"
                },
                {
                    name: "Delhi",
                    url: "https://routechef.com/search-form?from=Hyderabad+Airport+%28HYB%29&to=Delhi+Airport+%28DEL%29&dcode=DEL&dlat=28.566&dlng=77.103&stype=airport&dtype=airport&date=2026-03-27&adults=1&children=0&infants=0&seatClasses=Economy%2C3A&modes=Flight&nonstop=0"
                }
            ]
        },
        {
            name: "Ahmedabad Flights",
            image: ahmedabadImg,
            routes: [
                { name: "Mumbai", url: "https://routechef.com/search-form?from=Ahmedabad+Airport+%28AMD%29&to=Mumbai+Airport+%28BOM%29&dcode=BOM&slat=23.075&slng=72.632&dlat=19.089&dlng=72.868&stype=airport&dtype=airport&date=2026-03-27&adults=1&children=0&infants=0&seatClasses=Economy%2C3A&modes=Flight&nonstop=0" },
                { name: "Delhi", url: "https://routechef.com/search-form?from=Ahmedabad+Airport+%28AMD%29&to=Delhi+Airport+%28DEL%29&dcode=DEL&slat=23.075&slng=72.632&dlat=28.566&dlng=77.103&stype=airport&dtype=airport&date=2026-03-27&adults=1&children=0&infants=0&seatClasses=Economy%2C3A&modes=Flight&nonstop=0" },
                { name: "Bangalore", url: "https://routechef.com/search-form?from=Ahmedabad+Airport+%28AMD%29&to=Bangalore+Airport+%28BLR%29&dcode=BLR&slat=23.075&slng=72.632&dlat=12.95&dlng=77.668&stype=airport&dtype=airport&date=2026-03-27&adults=1&children=0&infants=0&seatClasses=Economy%2C3A&modes=Flight&nonstop=0" },
                { name: "Goa", url: "https://routechef.com/search-form?from=Ahmedabad+Airport+%28AMD%29&to=Goa+Airport+%28GOI%29&dcode=GOI&slat=23.075&slng=72.632&dlat=15.381&dlng=73.833&stype=airport&dtype=airport&date=2026-03-27&adults=1&children=0&infants=0&seatClasses=Economy%2C3A&modes=Flight&nonstop=0" }
            ]
        },
        {
            name: "Bangalore Flights",
            image: bangloreImg,
            routes: [
                { name: "Mumbai", url: "https://routechef.com/search-form?from=Bangalore+Airport+%28BLR%29&to=Mumbai+Airport+%28BOM%29&dcode=BOM&slat=12.95&slng=77.668&dlat=19.089&dlng=72.868&stype=airport&dtype=airport&date=2026-03-27&adults=1&children=0&infants=0&seatClasses=Economy%2C3A&modes=Flight&nonstop=0" },
                { name: "Delhi", url: "https://routechef.com/search-form?from=Bangalore+Airport+%28BLR%29&to=Delhi+Airport+%28DEL%29&dcode=DEL&slat=12.95&slng=77.668&dlat=28.566&dlng=77.103&stype=airport&dtype=airport&date=2026-03-27&adults=1&children=0&infants=0&seatClasses=Economy%2C3A&modes=Flight&nonstop=0" },
                { name: "Goa", url: "https://routechef.com/search-form?from=Bangalore+Airport+%28BLR%29&to=Goa+Airport+%28GOI%29&dcode=GOI&slat=12.95&slng=77.668&dlat=15.381&dlng=73.833&stype=airport&dtype=airport&date=2026-03-27&adults=1&children=0&infants=0&seatClasses=Economy%2C3A&modes=Flight&nonstop=0" },
                { name: "Hyderabad", url: "https://routechef.com/search-form?from=Bangalore+Airport+%28BLR%29&to=Hyderabad+Airport+%28HYB%29&dcode=HYB&slat=12.95&slng=77.668&stype=airport&dtype=airport&date=2026-03-27&adults=1&children=0&infants=0&seatClasses=Economy%2C3A&modes=Flight&nonstop=0" }
            ]
        },
        {
            name: "Pune Flights",
            image: puneImg,
            routes: [
                { name: "Goa", url: "https://routechef.com/search-form?from=Pune+Airport+%28PNQ%29&to=Goa+Airport+%28GOI%29&dcode=GOI&slat=18.582&slng=73.919&dlat=15.381&dlng=73.833&stype=airport&dtype=airport&date=2026-03-27&adults=1&children=0&infants=0&seatClasses=Economy%2C3A&modes=Flight&nonstop=0" },
                { name: "Delhi", url: "https://routechef.com/search-form?from=Pune+Airport+%28PNQ%29&to=Delhi+Airport+%28DEL%29&dcode=DEL&slat=18.582&slng=73.919&dlat=28.566&dlng=77.103&stype=airport&dtype=airport&date=2026-03-27&adults=1&children=0&infants=0&seatClasses=Economy%2C3A&modes=Flight&nonstop=0" },
                { name: "Bangalore", url: "https://routechef.com/search-form?from=Pune+Airport+%28PNQ%29&to=Bangalore+Airport+%28BLR%29&dcode=BLR&slat=18.582&slng=73.919&dlat=12.95&dlng=77.668&stype=airport&dtype=airport&date=2026-03-27&adults=1&children=0&infants=0&seatClasses=Economy%2C3A&modes=Flight&nonstop=0" },
                { name: "Nagpur", url: "https://routechef.com/search-form?from=Pune+Airport+%28PNQ%29&to=Nagpur+Airport+%28NAG%29&dcode=NAG&slat=18.582&slng=73.919&dlat=21.092&dlng=79.047&stype=airport&dtype=airport&date=2026-03-27&adults=1&children=0&infants=0&seatClasses=Economy%2C3A&modes=Flight&nonstop=0" }
            ]
        },
        {
            name: "Goa Flights",
            image: goaImg,
            routes: [
                { name: "Mumbai", url: "https://routechef.com/search-form?from=Goa+Airport+%28GOI%29&to=Mumbai+Airport+%28BOM%29&dcode=BOM&slat=15.381&slng=73.833&dlat=19.089&dlng=72.868&stype=airport&dtype=airport&date=2026-03-27&adults=1&children=0&infants=0&seatClasses=Economy%2C3A&modes=Flight&nonstop=0" },
                { name: "Delhi", url: "https://routechef.com/search-form?from=Goa+Airport+%28GOI%29&to=Delhi+Airport+%28DEL%29&dcode=DEL&slat=15.381&slng=73.833&dlat=28.566&dlng=77.103&stype=airport&dtype=airport&date=2026-03-27&adults=1&children=0&infants=0&seatClasses=Economy%2C3A&modes=Flight&nonstop=0" },
                { name: "Bangalore", url: "https://routechef.com/search-form?from=Goa+Airport+%28GOI%29&to=Bangalore+Airport+%28BLR%29&dcode=BLR&slat=15.381&slng=73.833&dlat=12.95&dlng=77.668&stype=airport&dtype=airport&date=2026-03-27&adults=1&children=0&infants=0&seatClasses=Economy%2C3A&modes=Flight&nonstop=0" },
                { name: "Hyderabad", url: "https://routechef.com/search-form?from=Goa+Airport+%28GOI%29&to=Hyderabad+Airport+%28HYB%29&dcode=HYB&slat=15.381&slng=73.833&stype=airport&dtype=airport&date=2026-03-27&adults=1&children=0&infants=0&seatClasses=Economy%2C3A&modes=Flight&nonstop=0" }
            ]
        }
    ];

    // const popularDomesticAirlines = [
    //     {
    //         name: "IndiGo",
    //         logo: indigoImg
    //     },
    //     {
    //         name: "Air India",
    //         logo: airIndiaImg
    //     },
    //     {
    //         name: "Air India Express",
    //         logo: airIndiaExpressImg
    //     },
    //     {
    //         name: "Akasa Air",
    //         logo: akashAirImg
    //     },
    //     {
    //         name: "Alliance Air",
    //         logo: allinceairImg
    //     },
    //     {
    //         name: "SpiceJet",
    //         logo: specjetImg
    //     }
    // ];

    useEffect(() => {
        const handleScroll = () => {
            const scrollPosition = window.scrollY;
            // Instead of changing opacity, we'll adjust the position of the gradient
            const gradientPosition = Math.min(0, -scrollPosition * 0.5);
            document.documentElement.style.setProperty('--gradient-position', `${gradientPosition}px`);
            // Decrease opacity more gradually by using a larger denominator
            const gradientOpacity = Math.max(0, 1 - scrollPosition / 1000);
            document.documentElement.style.setProperty('--gradient-opacity', gradientOpacity);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const handleHeroSearch = (e) => {
        e.preventDefault();
        navigate("/chat", { state: { fromHero: true, heroInput } });
    };

    return (
        <div className="home-container">
            {/* Navigation removed - now using global Navbar */}

            {/* Hero Section */}
            <section className="hero-section" style={{ overflow: "hidden" }}>
                {/* Signature route arc — one confident brand stroke, not texture.
                    Inline SVG: zero build/bundler dependencies. */}
                <svg
                    aria-hidden="true"
                    style={{ position: "absolute", top: 0, left: 0, width: "100%", height: 900, zIndex: 0, pointerEvents: "none" }}
                    viewBox="0 0 1440 900"
                    preserveAspectRatio="none"
                    fill="none"
                >
                    <defs>
                        <radialGradient id="rc-hero-glow" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse"
                            gradientTransform="translate(1180 340) scale(460 330)">
                            <stop offset="0" stopColor="#FF5B15" stopOpacity="0.10"/>
                            <stop offset="1" stopColor="#FF5B15" stopOpacity="0"/>
                        </radialGradient>
                    </defs>
                    <rect width="1440" height="900" fill="url(#rc-hero-glow)"/>
                    <path
                        d="M-80 880C340 840 560 720 800 700C1060 678 1150 430 1250 335"
                        stroke="#FF5B15" strokeOpacity="0.16" strokeWidth="3.5"
                        strokeLinecap="round" strokeDasharray="18 14"
                    />

                    <circle cx="1262" cy="324" r="11" stroke="#FF5B15" strokeOpacity="0.22" strokeWidth="3.5" fill="none"/>
                    <circle cx="1262" cy="324" r="3.5" fill="#FF5B15" fillOpacity="0.22"/>
                </svg>
                <div className="hero-content">

                    <h1 className="hero-title" style={{ fontSize: "clamp(30px, 5vw, 40px)", letterSpacing: "-0.5px", lineHeight: 1.15 }}>
                        One search for every journey
                    </h1>
                    <h2 className="hero-subtitle" style={{ fontSize: 16.5, fontWeight: 500, color: "#64748B", maxWidth: 560, margin: "8px auto 14px", lineHeight: 1.4 }}>
                        Search routes. Or let AI plan the whole trip.
                    </h2>

                    {/* Journey strip — in flow (never cropped/covered), contextual to the
                        active mode: routes → train/taxi/plane; AI trip → plane/places/hotel */}
                    <div aria-hidden="true" style={{ maxWidth: 520, width: "calc(100% - 48px)", margin: "4px auto 18px" }}>
                        <svg viewBox="0 0 520 56" width="100%" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: "block" }}>
                            {/* shared frame: origin dot, dashed segments, destination ring */}
                            <circle cx="16" cy="28" r="4.5" fill="#FF5B15" fillOpacity="0.5"/>
                            <path d="M28 28H126" stroke="#FF5B15" strokeOpacity="0.3" strokeWidth="2.6" strokeLinecap="round" strokeDasharray="7 7"/>
                            <path d="M178 28H266" stroke="#FF5B15" strokeOpacity="0.3" strokeWidth="2.6" strokeLinecap="round" strokeDasharray="7 7"/>
                            <path d="M318 28H406" stroke="#FF5B15" strokeOpacity="0.3" strokeWidth="2.6" strokeLinecap="round" strokeDasharray="7 7"/>
                            <path d="M458 28H484" stroke="#FF5B15" strokeOpacity="0.3" strokeWidth="2.6" strokeLinecap="round" strokeDasharray="7 7"/>
                            <circle cx="498" cy="28" r="8.5" stroke="#FF5B15" strokeOpacity="0.5" strokeWidth="3" fill="none"/>
                            <circle cx="498" cy="28" r="2.8" fill="#FF5B15" fillOpacity="0.5"/>

                            {searchTab === "routes" ? (
                                <>
                                    {/* train */}
                                    <g transform="translate(152 28)" fill="#FF5B15" fillOpacity="0.55">
                                        <rect x="-11" y="-13" width="22" height="16" rx="4.5"/>
                                        <rect x="-7.5" y="-9.5" width="6" height="5.5" rx="1.2" fill="#FFF9F5"/>
                                        <rect x="1.5" y="-9.5" width="6" height="5.5" rx="1.2" fill="#FFF9F5"/>
                                        <circle cx="-5.5" cy="6.5" r="2.4"/>
                                        <circle cx="5.5" cy="6.5" r="2.4"/>
                                    </g>
                                    {/* taxi */}
                                    <g transform="translate(292 29)" fill="#FF5B15" fillOpacity="0.55">
                                        <rect x="-3.5" y="-15" width="7" height="3" rx="1.2"/>
                                        <path d="M-6.5 -11.5h13l3.5 5.5h2.5a3 3 0 013 3v2a2.5 2.5 0 01-2.5 2.5h-26A2.5 2.5 0 01-15.5 -1v-2a3 3 0 013-3h2z"/>
                                        <path d="M-5 -9h4v3.2h-6.3z" fill="#FFF9F5"/>
                                        <path d="M1 -9h4l2 3.2H1z" fill="#FFF9F5"/>
                                        <circle cx="-8" cy="2.5" r="2.5"/>
                                        <circle cx="8" cy="2.5" r="2.5"/>
                                    </g>
                                    {/* airplane (real silhouette, nose right) */}
                                    <g transform="translate(432 28) rotate(90) scale(1.2) translate(-12 -12)" fill="#FF5B15" fillOpacity="0.55">
                                        <path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"/>
                                    </g>
                                </>
                            ) : (
                                <>
                                    {/* airplane — getting there */}
                                    <g transform="translate(152 28) rotate(90) scale(1.2) translate(-12 -12)" fill="#FF5B15" fillOpacity="0.55">
                                        <path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"/>
                                    </g>
                                    {/* camera — places to visit / sightseeing */}
                                    <g transform="translate(292 28)" fill="#FF5B15" fillOpacity="0.55">
                                        <path d="M-11 -7.5h4.2l2.4-3.4h8.8l2.4 3.4H11a3 3 0 013 3v9.5a3 3 0 01-3 3h-22a3 3 0 01-3-3v-9.5a3 3 0 013-3z"/>
                                        <circle cx="0" cy="0.8" r="5.4" fill="#FFF9F5"/>
                                        <circle cx="0" cy="0.8" r="2.6"/>
                                        <rect x="6.6" y="-5" width="3.4" height="2.4" rx="1" fill="#FFF9F5"/>
                                    </g>
                                    {/* hotel — staying */}
                                    <g transform="translate(432 28)" fill="#FF5B15" fillOpacity="0.55">
                                        <rect x="-11" y="-14" width="22" height="27" rx="2.5"/>
                                        <rect x="-7.5" y="-10" width="5" height="5" rx="1" fill="#FFF9F5"/>
                                        <rect x="2.5" y="-10" width="5" height="5" rx="1" fill="#FFF9F5"/>
                                        <rect x="-7.5" y="-2" width="5" height="5" rx="1" fill="#FFF9F5"/>
                                        <rect x="2.5" y="-2" width="5" height="5" rx="1" fill="#FFF9F5"/>
                                        <rect x="-2.5" y="6" width="5" height="7" rx="1" fill="#FFF9F5"/>
                                    </g>
                                </>
                            )}
                        </svg>
                    </div>
                    {/* <p className="hero-description">AI-powered route planner combining trains and flights for hassle-free travel in India.</p> */}

                    {/* ── Mode switch: segmented control (tab semantics — the forms
                        below own the actual CTA buttons). Fully inline-styled so
                        mobile media queries can't distort it. ── */}
                    <div
                        role="tablist"
                        aria-label="Search mode"
                        style={{
                            display: "flex", alignItems: "stretch", gap: 4,
                            margin: "0 auto 28px", position: "relative", zIndex: 10,
                            maxWidth: 640, width: "calc(100% - 32px)",
                            background: "#F1F5F9", border: "1px solid #E2E8F0",
                            borderRadius: 16, padding: 5, boxSizing: "border-box",
                        }}
                    >
                        {[
                            {
                                k: "routes", icon: "search", label: "Route Search", beta: false,
                                desc: "Trains, Buses & flights — confirmed-seat routes",
                            },
                            {
                                k: "trip", icon: "sparkles", label: "AI Trip Planner", beta: true,
                                desc: "Routes + places + hotels + day-wise itinerary",
                            },
                            ...(FEATURES.ENABLE_CREATOR_TRIPS ? [{
                                k: "creator", icon: "sparkles", label: "Creator Trips", beta: true,
                                desc: "Book a creator's trip from your city",
                            }] : []),
                        ].map(t => {
                            const active = searchTab === t.k;
                            return (
                                <button
                                    key={t.k}
                                    type="button"
                                    role="tab"
                                    aria-selected={active}
                                    className="rc-mode-tab"
                                    onClick={() => setSearchTab(t.k)}
                                    style={{
                                        flex: "1 1 0", minWidth: 0,
                                        display: "flex", flexDirection: "column",
                                        alignItems: "center", justifyContent: "center", gap: 3,
                                        padding: "10px 12px", borderRadius: 12,
                                        border: "none", cursor: "pointer",
                                        background: active ? "#FFFFFF" : "transparent",
                                        boxShadow: active ? "0 2px 8px rgba(15, 23, 42, 0.08)" : "none",
                                        transition: "all 0.2s ease",
                                        WebkitTapHighlightColor: "transparent",
                                        fontFamily: "'Inter', sans-serif",
                                    }}
                                >
                                    <span className="rc-tab-title" style={{
                                        display: "inline-flex", alignItems: "center", gap: 7,
                                        fontWeight: 700, fontSize: 15, lineHeight: 1.2,
                                        color: active ? "#FF4E01" : "#334155",
                                        whiteSpace: "nowrap",
                                    }}>
                                        <span aria-hidden="true" style={{ display: "inline-flex" }}>
                                            {t.icon === "sparkles" ? (
                                                <Sparkles
                                                    size={16}
                                                    color={active ? "#FF4E01" : "#6B3FA0"}
                                                    fill={active ? "#FF4E01" : "#6B3FA0"}
                                                    strokeWidth={1.5}
                                                />
                                            ) : (
                                                <SearchIcon
                                                    size={15}
                                                    color={active ? "#FF4E01" : "#475569"}
                                                    strokeWidth={2.6}
                                                />
                                            )}
                                        </span>
                                        {t.label}
                                        {t.beta && (
                                            <span style={{
                                                fontSize: 9.5, fontWeight: 800, letterSpacing: 0.6, lineHeight: 1.4,
                                                padding: "2px 7px", borderRadius: 99,
                                                background: active ? "#F0EBF9" : "#E9E2F5",
                                                color: "#6B3FA0",
                                            }}>BETA</span>
                                        )}
                                    </span>
                                    <span className="rc-tab-desc" style={{
                                        fontWeight: 500, fontSize: 11.5, lineHeight: 1.35,
                                        color: active ? "#64748B" : "#94A3B8",
                                        textAlign: "center",
                                    }}>{t.desc}</span>
                                </button>
                            );
                        })}
                    </div>

                    {/* Search Routes (existing, unchanged) */}
                    {searchTab === "routes" && <FormSearchSection />}

                    {/* Create a Trip (beta) → /trip-planner */}
                    {searchTab === "trip" && <TripSearchSection />}

                    {/* Creator Trips → /c/:handle/:slug */}
                    {FEATURES.ENABLE_CREATOR_TRIPS && searchTab === "creator" && <CreatorTripsTeaser />}

                </div>

            </section>

            {/* Features Section */}
            <section className="features-section" id="features">
                <div className="section-header">
                    <h3>Features</h3>
                    <h2>Why Routechef?</h2>
                    <p>Routechef intelligently combines trains and flights to maximize your chances of confirmed travel.</p>
                </div>

                <RouteCard className="card-style" />

                <AIChatAssistantCard className="card-style" />

                <Card3 className="card-style" />
            </section>

            {/* How It Works Section */}
            <section className="how-it-works">
                <h3>How It Works?</h3>
                <h2>How Routechef Gets You There</h2>
                <div className="steps-container">
                    <div className="step">
                        <div className="step-number">1</div>
                        <h3>Enter Location</h3>
                        <p>Tell us where you're starting and going.</p>
                    </div>
                    <div className="step">
                        <div className="step-number">2</div>
                        <h3>Select Travel Modes</h3>
                        <p>Choose your preferred travel modes: trains, flights.</p>
                    </div>
                    <div className="step">
                        <div className="step-number">3</div>
                        <h3>Get Routes & Book</h3>
                        <p>Explore smart routes with confirmed seat options.</p>
                    </div>
                </div>

                <Card4 className="card-style" />
            </section>

            {/* Testimonials Section */}
            <section className="testimonials-section">
                <h3>Testimonials</h3>
                <h2>Loved by Travelers!</h2>
                <div className="testimonials-container">
                    <div className="testimonial-card">
                        <p className="testimonial-text">Confirmed seat routes are a lifesaver routechef makes planning so easy</p>
                        <div className="testimonial-author">
                            <div className="author-info">
                                <h4>Lucky Dehariya</h4>
                                <div className="rating">⭐⭐⭐⭐⭐</div>
                            </div>
                        </div>
                    </div>
                    <div className="testimonial-card">
                        <p className="testimonial-text">Good break journey details display</p>
                        <div className="testimonial-author">
                            <div className="author-info">
                                <h4>Hrishabh Kishore</h4>
                                <div className="rating">⭐⭐⭐⭐⭐</div>
                            </div>
                        </div>
                    </div>
                    <div className="testimonial-card">
                        <p className="testimonial-text">The app found the nearest station from a small town easily best travel planing app</p>
                        <div className="testimonial-author">
                            <div className="author-info">
                                <h4>Sajit Qureshi</h4>
                                <div className="rating">⭐⭐⭐⭐⭐</div>
                            </div>
                        </div>
                    </div>
                    <div className="testimonial-card">
                        <p className="testimonial-text">Only app I have came through which shows shortest route for break journeys and also have multiple modes of break journey, very helpful in times where direct ticket is not available</p>
                        <div className="testimonial-author">
                            <div className="author-info">
                                <h4>Shri Balaji</h4>
                                <div className="rating">⭐⭐⭐⭐⭐</div>
                            </div>
                        </div>
                    </div>
                    <div className="testimonial-card">
                        <p className="testimonial-text">Great app very user friendly and break journey also available 😊</p>
                        <div className="testimonial-author">
                            <div className="author-info">
                                <h4>Soumavo Mal</h4>
                                <div className="rating">⭐⭐⭐⭐⭐</div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="stats-bar">
                    <div className="stat">
                        <h3>10,000+</h3>
                        <p>routes planned</p>
                    </div>
                    <div className="stat">
                        <h3>90%+</h3>
                        <p>confirmation success rate</p>
                    </div>
                    <div className="stat">
                        <h3>4.3/5</h3>
                        <p>average user rating</p>
                    </div>
                </div>
            </section>

            {/* Popular Routes Section */}
            <section className="max-w-[1350px] mx-auto w-full pt-10 pb-2 md:pt-[60px] md:pb-[10px] px-5">
                <div className="text-center">
                    <h3 className="text-[#FF5B15] text-[1.1rem] md:text-[1.2rem] mb-2 md:mb-[10px] uppercase font-bold font-['Outfit',sans-serif]">Top Collections</h3>
                    <h2 className="text-[1.6rem] md:text-[2rem] mb-6 md:mb-[40px] font-bold font-['Outfit',sans-serif]">Popular Flight Routes</h2>
                </div>
                {/* Popular Flight Routes */}
                <div className=" text-left">
                    {/* <h2 className="text-[22px] font-bold text-[#1F2937] mb-6 font-['Plus_Jakarta_Sans',sans-serif]">Popular Flight Routes</h2> */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {popularFlightRoutes.map((route, idx) => (
                            <div key={idx} className="flex flex-row items-center p-3 rounded-xl border border-gray-200 bg-white shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-md transition-shadow">
                                <div className="w-[60px] h-[60px] flex-shrink-0 mr-4">
                                    <img src={route.image} alt={route.name} loading="lazy" className="w-full h-full object-cover rounded-lg" />
                                </div>
                                <div className="flex flex-col">
                                    <h4 className="font-medium text-gray-800 text-sn md:text-base lg:text-lg m-0 mb-1">{route.name}</h4>
                                    <p className="text-sm md:text-sm lg:text-base text-gray-500 m-0 font-medium flex flex-wrap gap-[4px] items-center">
                                        To:
                                        <span className="text-[#FF4E01] flex items-center flex-wrap gap-[4px]">
                                            {route.routes.map((destination, i) => (
                                                <React.Fragment key={i}>
                                                    {i > 0 && <span className="text-gray-400">•</span>}
                                                    <a
                                                        href={destination.url}
                                                        rel="noopener noreferrer"
                                                        className="hover:underline text-[#FF4E01] transition-all cursor-pointer"
                                                        onClick={(e) => {
                                                            // Prevent empty link jumping to top of page
                                                            if (destination.url === '#' || !destination.url) {
                                                                e.preventDefault();
                                                            }
                                                        }}
                                                    >
                                                        {destination.name}
                                                    </a>
                                                </React.Fragment>
                                            ))}
                                        </span>
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Popular Airlines */}
                {/* <div className="mb-16 text-left">
                    <h2 className="text-[22px] font-bold text-[#1F2937] mb-6 font-['Plus_Jakarta_Sans',sans-serif]">Popular Domestic Airlines</h2>
                    <div className="flex flex-wrap justify-between items-center gap-6 px-4 py-8 rounded-[20px] border border-gray-200 bg-white shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
                        {popularDomesticAirlines.map((airline, idx) => (
                            <div key={idx} className="flex flex-col items-center flex-shrink-0 w-24 sm:w-28 md:w-32 cursor-pointer group px-2">
                                <div className="h-10 sm:h-12 w-full flex items-center justify-center mb-3">
                                    <img 
                                        src={airline.logo} 
                                        alt={airline.name} 
                                        className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform duration-300" 
                                        onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }} 
                                    />
                                    <span style={{ display: 'none' }} className="font-bold text-gray-600 text-center">{airline.name}</span>
                                </div>
                                <span className="text-[13px] sm:text-sm font-semibold text-[#334155] text-center whitespace-nowrap">{airline.name}</span>
                            </div>
                        ))}
                    </div>
                </div> */}
            </section>

            {/* SEO discovery section — routes users can search immediately */}
            <section className="max-w-[1350px] mx-auto w-full px-5 pt-12 pb-6">
                <div className="rounded-2xl border border-orange-100 bg-orange-50/40 p-6 md:p-8">
                    <div className="text-center max-w-2xl mx-auto">
                        <p className="text-[#FF5B15] text-sm uppercase font-bold tracking-wide mb-2">Explore travel guides</p>
                        <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">Find the best way to reach your destination</h2>
                        <p className="text-slate-600 text-sm md:text-base leading-relaxed">
                            Compare flight, bus, train and mixed-mode travel options for popular Indian city pairs.
                            Choose a route guide, select your date and search current availability on RouteChef.
                        </p>
                    </div>
                    <div className="flex flex-wrap justify-center gap-3 mt-6">
                        {[
                            ['Mumbai to Munnar', '/how-to-reach/mumbai-to-munnar'],
                            ['Mumbai to Varkala', '/how-to-reach/mumbai-to-varkala'],
                            ['Bangalore to Coorg', '/how-to-reach/bangalore-to-coorg'],
                            ['Delhi to Rishikesh', '/how-to-reach/delhi-to-rishikesh'],
                            ['Mumbai to Goa', '/how-to-reach/mumbai-to-goa'],
                            ['Bangalore to Goa', '/how-to-reach/bangalore-to-goa'],
                            ['All travel guides', '/how-to-reach'],
                        ].map(([label, url]) => (
                            <Link
                                key={url}
                                to={url}
                                className="rounded-full border border-orange-200 bg-white px-4 py-2 text-sm font-semibold text-[#C2410C] hover:border-[#FF4E01] hover:text-[#FF4E01] transition-colors"
                            >
                                {label}
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <section className="faq-section" id="faqs">
                <h3>FAQs</h3>
                <h2>Frequently Asked Question's</h2>
                <div className="faq-container">
                    {faqs.map((faq, idx) => (
                        <div
                            className={`faq-item${activeFaq === idx ? " active" : ""}`}
                            key={idx}
                            role="button"
                            tabIndex={0}
                            aria-expanded={activeFaq === idx}
                            onClick={() => setActiveFaq(activeFaq === idx ? null : idx)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    setActiveFaq(activeFaq === idx ? null : idx);
                                }
                            }}
                        >
                            <div className="faq-question">
                                <h4>{faq.question}</h4>
                                <span className="faq-toggle">
                                    {activeFaq === idx ? <FaMinus /> : <FaPlus />}
                                </span>
                            </div>
                            <div className="faq-answer">{faq.answer}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Footer */}
            <footer className="footer" id="contact">
                <div className="footer-content">
                    <div className="footer-brand">
                        <span><span style={{ color: '#FF5B15', fontWeight: '600', fontSize: '24px' }}>Route</span><span style={{ color: '#0A0A0A', fontWeight: '400', fontSize: '24px' }}>chef</span></span>
                        <p style={{ fontWeight: '400', color: '#0A0A0A' }}>Your AI Powered Route Planner for Confirmed Journeys.</p>
                    </div>
                    <div className="footer-links">
                        <h4>Links</h4>
                        <ul>
                            <li><a href="#features">Features</a></li>
                            <li><a href="#faqs">FAQs</a></li>
                            <li><Link to="/how-to-reach">How to Reach Guides</Link></li>
                            <li><Link to="/how-to-reach/bangalore-to-coorg">Bangalore to Coorg</Link></li>
                            <li><Link to="/how-to-reach/mumbai-to-munnar">Mumbai to Munnar</Link></li>
                            <li><Link to="/how-to-reach/mumbai-to-rishikesh">Mumbai to Rishikesh</Link></li>
                            <li><Link to="/how-to-reach/bangalore-to-varkala">Bangalore to Varkala</Link></li>
                            <li><Link to="/how-to-reach/mumbai-to-varkala">Mumbai to Varkala</Link></li>
                            {/* <li><a href="#terms">Terms & Conditions</a></li>
                            <li><a href="#privacy">Privacy policy</a></li> */}
                        </ul>
                    </div>
                    <div className="footer-download">
                        <h4>Scan to Download App</h4>
                        <div className="qr-code">
                            <img src="/qrcode.png" alt="Download QR Code" loading="lazy" />
                        </div>
                        <p>Contact: <a href="mailto:info@routechef.com">info@routechef.com</a></p>
                    </div>
                </div>
                <div className="footer-bottom">
                    <div className="social-icons">
                        {/* <a href="#" className="social-icon">X</a> */}
                        <a href="https://www.facebook.com/routechef/" className="social-icon" target="_blank" rel="noopener noreferrer" onMouseUp={e => e.currentTarget.blur()} onTouchEnd={e => e.currentTarget.blur()}>
                            <FaFacebookF />
                        </a>
                        <a href="https://www.linkedin.com/company/105943037" className="social-icon" target="_blank" rel="noopener noreferrer" onMouseUp={e => e.currentTarget.blur()} onTouchEnd={e => e.currentTarget.blur()}>
                            <FaLinkedinIn />
                        </a>
                        {/* <a href="#" className="social-icon">yt</a> */}
                        <a href="https://www.instagram.com/routechef/" className="social-icon" target="_blank" rel="noopener noreferrer" onMouseUp={e => e.currentTarget.blur()} onTouchEnd={e => e.currentTarget.blur()}>
                            <FaInstagram />
                        </a>
                    </div>
                    <p className="copyright">© 2025 Routechef</p>
                    <div className="footer-extra">
                        <span>Made with ❤️ in India</span>
                    </div>
                </div>
            </footer>
        </div>
    );
};

/* Home tab content for Creator Trips: featured creator trips, one tap to book from your city */
const CreatorTripsTeaser = () => {
    const creators = Object.values(CREATORS);
    return (
        <section style={{ maxWidth: 900, width: "calc(100% - 32px)", margin: "0 auto", position: "relative", zIndex: 5 }}>
            {creators.map(c => (
                <div key={c.handle} style={{ textAlign: "left" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
                        <div style={{ fontWeight: 700, color: "#0F172A", fontSize: 15 }}>
                            Trips by {c.name} <span style={{ color: "#64748B", fontWeight: 500 }}>@{c.handle} · {c.followers}</span>
                        </div>
                        <Link to={`/c/${c.handle}`} style={{ color: "#FF4E01", fontWeight: 700, fontSize: 13, textDecoration: "none" }}>See storefront →</Link>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 12 }}>
                        {getCreatorTrips(c.handle).map(t => (
                            <Link key={t.slug} to={`/c/${c.handle}/${t.slug}`} style={{ textDecoration: "none", color: "inherit", background: "#fff", border: "1px solid #E2E8F0", borderRadius: 14, overflow: "hidden" }}>
                                <div style={{ height: 110, backgroundImage: `url(${t.cover})`, backgroundSize: "cover", backgroundPosition: "center" }} />
                                <div style={{ padding: "10px 12px 12px" }}>
                                    <div style={{ fontWeight: 700, fontSize: 14, color: "#0F172A" }}>{t.title}</div>
                                    <div style={{ fontSize: 12, color: "#64748B", marginTop: 3 }}>{t.subtitle}</div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            ))}
        </section>
    );
};

export default Home2;
